import {
  Container,
  Distribute,
  Icon,
  IconButton,
  Layout,
  Spacer,
  SvgClose,
  Text,
  TextButton,
} from '@loomhq/lens';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  dismissElementHandler,
  fadeOutCard,
  isAutoCompleteExcluded,
  isIntroCard,
} from '../utilities';
import { useGetSelectedWorkspace } from '../../../../hooks/workspace';
import { useGetSelectedWorkspaceOnboarding } from '../../../../hooks/workspaceOnboarding';
import { WORKSPACE_ELEMENT_COMPLETE } from '../../../../../../shared/constants/workspaceOnboarding';

import styles from './styles.module.less';

export const DashboardOnboardingSecondaryAction = ({
  icon,
  onClick,
  children,
}) => {
  return (
    <TextButton
      size="small"
      icon={<Icon size={2} color="grey6" icon={icon} />}
      onClick={onClick}
    >
      <Text size="small" color="grey6" fontWeight="bold">
        {children}
      </Text>
    </TextButton>
  );
};

export const DashboardLibraryIntroCard = ({ heading, description }) => {
  const selectedWorkspace = useGetSelectedWorkspace();

  if (selectedWorkspace === undefined) {
    return null;
  }

  return (
    <Container
      width="auto"
      maxWidth={54}
      height={26}
      radius="medium"
      paddingRight={2.5}
      paddingTop="large"
      paddingBottom="large"
      backgroundColor="transparent"
      className={styles.dashboardOnboardingCardCompact}
    >
      <Container width="100%" paddingLeft={0.5}>
        {heading && (
          <div className={styles.dashboardIntoCardHeader}>
            <Text color="#1c294f" htmlTag="div" fontWeight="bold">
              {heading}
            </Text>
          </div>
        )}
        {description && (
          <>
            <Spacer top={0.5} />
            <Text color="#1c294f">{description}</Text>
          </>
        )}
      </Container>
      <Spacer top="medium" />
    </Container>
  );
};

const DashboardOnboardingCardCompact = props => {
  const {
    heading,
    description,
    primaryAction,
    secondaryAction,
    showCloseCompact = true, // by default we show dismiss icons
    centerpiece,
    complete,
    cardKey,
    cardConfig,
    workspaceId,
    completeMessage,
  } = props;

  const dispatch = useDispatch();

  const dimissHandler = () =>
    dismissElementHandler(dispatch, cardKey, workspaceId);

  const [showCard, setShowCard] = useState(true);
  const [shouldFadeOut, setShouldFadeOut] = useState(false);

  if (complete && !isAutoCompleteExcluded(cardConfig, cardKey)) {
    fadeOutCard(
      dispatch,
      cardConfig,
      setShouldFadeOut,
      setShowCard,
      dimissHandler
    );
  }

  // Do not show dismiss button for intro cards in compact mode
  const showDismiss = showCloseCompact && !isIntroCard(cardKey);

  if (!showCard) {
    return null;
  }

  return (
    <Container
      width="auto"
      height={26}
      maxWidth={54}
      radius="medium"
      padding="large"
      backgroundColor="background"
      className={classnames(
        styles.dashboardOnboardingCardCompact,
        shouldFadeOut ? styles.dashboardOnboardingCardCompletionFade : null
      )}
    >
      {complete && (
        <>
          <Container
            className={styles.dashboardOnboardingCardCompletionOverlay}
          />
          <Container
            padding="large"
            className={styles.dashboardOnboardingCardCompletionCompactContent}
          >
            <Text color="white" fontWeight="bold" size="large">
              {completeMessage}
            </Text>
            <Spacer top={1.5} />
          </Container>
        </>
      )}
      {showDismiss && (
        <div className={styles.dashboardOnboardingCardClose}>
          <IconButton
            altText="Dismiss"
            size="small"
            icon={<SvgClose />}
            iconColor="grey5"
            onClick={dimissHandler}
          />
        </div>
      )}
      <Distribute
        isSpread
        direction="column"
        className={styles.dashboardOnboardingCardContainer}
      >
        <Container width="100%">
          <Layout
            direction="row"
            className={styles.dashboardOnboardingCardActions}
            gap="medium"
            isSpread
          >
            <Layout.Section>
              {heading && (
                <Text size="large" fontWeight="bold">
                  {heading}
                </Text>
              )}
              {description && <Text color="bodyDimmed">{description}</Text>}
            </Layout.Section>
            <Layout.Section width="26%">
              <Distribute
                className={styles.dashboardOnboardingCompactCardImage}
              >
                {centerpiece}
              </Distribute>
            </Layout.Section>
          </Layout>
        </Container>
        <Distribute direction="row" gap="medium" alignment="center">
          {primaryAction}
          {secondaryAction}
        </Distribute>
      </Distribute>
    </Container>
  );
};

// TODO(@vinibaggio): Remove me!
export const DashboardOnboardingCardFull = props => {
  const {
    heading,
    description,
    secondaryAction,
    primaryAction,
    centerpiece,
    showClose = false,
    complete,
    cardKey,
    cardConfig,
    workspaceId,
    completeMessage,
  } = props;

  const dispatch = useDispatch();
  const dimissHandler = () =>
    dismissElementHandler(dispatch, cardKey, workspaceId);

  const [showCard, setShowCard] = useState(true);
  const [shouldFadeOut, setShouldFadeOut] = useState(false);

  const showAsComplete = complete && !isAutoCompleteExcluded(cardKey);

  if (showAsComplete) {
    fadeOutCard(
      dispatch,
      cardConfig,
      setShouldFadeOut,
      setShowCard,
      dimissHandler
    );
  }

  if (!showCard) {
    return null;
  }

  return (
    <Container
      height={50}
      width={32}
      className={classnames(
        styles.dashboardOnboardingCard,
        shouldFadeOut ? styles.dashboardOnboardingCardCompletionFade : null
      )}
    >
      {showAsComplete && (
        <>
          <Container
            borderSide="all"
            radius="medium"
            borderColor="grey3"
            className={styles.dashboardOnboardingCardCompletionOverlay}
          />
          <Container
            padding="large"
            className={styles.dashboardOnboardingCardCompletionContent}
          >
            <Text color="white" fontWeight="bold" size="large">
              {completeMessage}
            </Text>
            <Spacer top="large" />
          </Container>
        </>
      )}
      <Container
        width="100%"
        height="100%"
        borderSide="all"
        radius="medium"
        borderColor="grey2"
        padding="large"
        className={styles.dashboardOnboardingCard}
        backgroundColor="background"
      >
        {showClose && (
          <div className={styles.dashboardOnboardingCardClose}>
            <IconButton
              altText="Dismiss"
              size="small"
              icon={<SvgClose />}
              iconColor="grey5"
              onClick={dimissHandler}
            />
          </div>
        )}
        <Distribute
          direction="column"
          alignment="center"
          isSpread
          className={styles.dashboardOnboardingCardContainer}
        >
          <Container width="100%">
            <Distribute direction="column" alignment="start">
              {heading && (
                <Text size="large" fontWeight="bold">
                  {heading}
                </Text>
              )}
              <Spacer top="small" />
              {description && <Text color="bodyDimmed">{description}</Text>}
            </Distribute>
          </Container>
          <Container height="auto">
            <div className={styles.dashboardOnboardingFullCardCenterpiece}>
              {centerpiece}
            </div>
            <Spacer top={4} />
            <Container width="100%">
              <Distribute direction="column" gap={1.5} alignment="center">
                {secondaryAction || <Spacer top={3} />}
                {primaryAction && (
                  <div
                    className={styles.dashboardOnboardingCardFullWidthButton}
                  >
                    {primaryAction}
                  </div>
                )}
              </Distribute>
            </Container>
          </Container>
        </Distribute>
      </Container>
    </Container>
  );
};

function DashboardOnboardingCard(props) {
  const { cardKey } = props;

  const cardStates = useGetSelectedWorkspaceOnboarding();
  const selectedWorkspace = useGetSelectedWorkspace();

  if (cardStates === undefined || selectedWorkspace === undefined) {
    return null;
  }

  const cardCompleted = cardStates[cardKey] === WORKSPACE_ELEMENT_COMPLETE;

  return (
    <DashboardOnboardingCardCompact
      complete={cardCompleted}
      workspaceId={selectedWorkspace.id}
      {...props}
    />
  );
}

export default DashboardOnboardingCard;
